import { useId } from 'react';
import { Clock2, Clock5, Clock8, Clock11, Volume, Volume1, Volume2 } from 'lucide-react';
import type { ComparisonScentProfile } from '../../../../packages/shared/src/comparison-evidence.js';
import { longevityBands } from '../../../../packages/shared/src/brand-landing.js';
import { accordKey } from '../../../../packages/shared/src/accord-filter.js';
export type ProfileFragrance={id?:string;name:string;profile?:ComparisonScentProfile|null};
export type ComparisonProps={original:ProfileFragrance;alternative:ProfileFragrance;relationshipType?:string};

/** Unlisted accords compare as zero in the recorded profile; this is not a chemical absence claim. */
export function compareAccords(original:ComparisonScentProfile|null|undefined,alternative:ComparisonScentProfile|null|undefined,limit=12){
 if(!original?.accords.length||!alternative?.accords.length)return [];
 const entries=new Map<string,{name:string;original:number|null;alternative:number|null;originalDescribed:boolean;alternativeDescribed:boolean}>();
 for(const [side,profile]of [['original',original],['alternative',alternative]] as const){
  for(const accord of profile?.accords??[]){
   const key=canonicalAccord(accord.name);
   const row=entries.get(key)??{name:accordLabels[key]??accord.name,original:0,alternative:0,originalDescribed:false,alternativeDescribed:false};
   row[side]=accord.intensity;row[side==='original'?'originalDescribed':'alternativeDescribed']=true;entries.set(key,row);
  }
 }
 return [...entries.values()].filter(row=>row.original!==null&&row.alternative!==null&&(row.original>0||row.alternative>0)).sort((a,b)=>Math.max(b.original!,b.alternative!)-Math.max(a.original!,a.alternative!)).slice(0,Math.max(1,Math.min(12,limit)));
}
const accordLabels:Record<string,string>={leather:'Leather',woody:'Woody',musky:'Musky',aquatic:'Aquatic','warm spicy':'Warm Spicy'};
/** The same spelling rule the catalogue's accord filter uses, so a row and a filter mean one accord. */
export const canonicalAccord=accordKey;
export function IntensityScale({value,tone}:{value:number|null;tone:'original'|'alternative'}){
 if(value===null||!Number.isInteger(value)||value<0||value>5)return null;
 const score=value;
 const label=score===0?'0 out of 5, not listed in the recorded profile':score+' out of 5, editorial intensity estimate';
 return <span className={'intensity-scale '+tone} role="img" aria-label={label} title={label}><span className="intensity-segments" aria-hidden="true">{[1,2,3,4,5].map(step=><i key={step} className={step<=score?'filled':''}/>)}</span><span className="intensity-value" aria-hidden="true">{score}/5</span></span>;
}

export function FragranceScentRatings({profile,name}:{profile:ComparisonScentProfile|null|undefined;name?:string}){
 const heading=useId(),wear=useId(),note=useId();
 // Strongest first, so the scent's overall shape reads at a glance; ties keep their recorded order.
 const accords=(profile?.accords??[]).filter(accord=>accord.intensity!==null&&Number.isInteger(accord.intensity)&&accord.intensity>=0&&accord.intensity<=5).sort((a,b)=>b.intensity!-a.intensity!);
 const performance=(['projection','longevity'] as const).filter(measure=>profile?.[measure]);
 if(!accords.length&&!performance.length)return null;
 // Accord prominence and wear estimates share one record, but their scales remain separate inside
 // the panel so projection and longevity never read as more accord scores.
 // What it smells of and how it wears are two records on two scales, so each keeps its own card and
 // neither has to be read through the other. The note covers both scales, and trails whichever card
 // comes last, so it always sits directly beneath the column it belongs to.
 const scaleNote=<p id={note} className="profile-note">{accords.length?'Accord prominence runs from 1 (a background trace) to 5 (defining). ':''}{performance.length?'Performance records are editorial estimates; wear varies by person. ':''}<a href="/about#ratings">About these scores</a></p>;
 return <section className="fragrance-scent-ratings" aria-labelledby={accords.length?heading:wear}>
  {accords.length?<div className="profile-column">
   <div className="profile-panel scent-panel">
    <h2 id={heading}>Scent profile</h2>
    <table className="profile-table" aria-labelledby={heading} aria-describedby={note}>
     <caption className="sr-only">Scent accords{name?': '+name:''}</caption>
     <thead><tr><th scope="col">Accord</th><th scope="col" className="original-column">{name?<span className="column-name">{name}</span>:'Prominence'}</th></tr></thead>
     <tbody>{accords.map(accord=><tr key={canonicalAccord(accord.name)}><th scope="row">{accordLabels[canonicalAccord(accord.name)]??accord.name}</th><td><IntensityScale value={accord.intensity} tone="original"/></td></tr>)}</tbody>
    </table>
   </div>
   {performance.length?null:scaleNote}
  </div>:null}
  {performance.length?<div className="profile-column">
   <div className="profile-panel single-performance" aria-labelledby={wear} aria-describedby={note}>
    <h2 id={wear}>Performance</h2>
    <dl>{performance.map(measure=><div className="single-performance-row" key={measure}>
     <dt>{measure==='projection'?'Projection':'Longevity'}</dt>
     <dd><PerformanceScale measure={measure} value={profile?.[measure]} tone="original"/></dd>
    </div>)}</dl>
   </div>
   {scaleNote}
  </div>:null}
 </section>;
}

// Hour bands make the broad wear categories easier to scan. The evidence beneath the table keeps
// version-specific variation visible, so the bands remain estimates rather than guarantees.
export const performanceSteps={projection:['Soft','Moderate','Strong'],longevity:['Light','Moderate','Long-lasting','Very long-lasting']} as const;
const projectionIcons:Record<string,typeof Volume>={Soft:Volume,Moderate:Volume1,Strong:Volume2};
const longevityIcons:Record<string,typeof Clock2>={Light:Clock2,Moderate:Clock5,'Long-lasting':Clock8,'Very long-lasting':Clock11};
const longevityDisplay:Record<string,{band:string;icon:typeof Clock2}>=Object.fromEntries(Object.entries(longevityBands).map(([value,band])=>[value,{band,icon:longevityIcons[value]}]));
/** The mark the chart gives a recorded band, so a row naming that band elsewhere carries the same one. */
export const performanceIcon=(measure:keyof typeof performanceSteps,value:string|null|undefined)=>
 !value?undefined:measure==='longevity'?longevityIcons[value]:projectionIcons[value];
export function PerformanceScale({measure,value,tone}:{measure:keyof typeof performanceSteps;value:string|null|undefined;tone:'original'|'alternative'}){
 if(!value)return <span className="estimate-unknown">Not yet assessed</span>;
 const longevity=measure==='longevity'?longevityDisplay[value]:undefined;
 const Icon=longevity?.icon??projectionIcons[value];
 return <span className={'performance-value '+tone}>
  {Icon&&<Icon className="performance-icon" size={17} aria-hidden="true"/>}
  <span className="performance-readout">
   <span className="performance-label">{value}</span>
   {longevity&&<span className="performance-band">{longevity.band}</span>}
  </span>
 </span>;
}
